import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from ".";
import type { CourseWordLessonDetail } from "@/models/course-word-lesson-detail";
import type { CompleteLesson } from "@/models/course-word-result";

interface WordResult {
  word: string;
  spentTime: number;
  correct?: boolean;
  selectedAnswer?: string;
  existOnWordbook?: boolean;
  existOnWordPractice?: boolean;
}

interface LessonResult {
  courseId?: string;
  partId?: string;
  lessonId?: string;
  totalSpentTime?: number;
  words: WordResult[];
}

interface WordState {
  courseWordLessonDetail: CourseWordLessonDetail | null;
  reviewCourseWordLessonDetail: CourseWordLessonDetail | null;
  lessonResult: LessonResult | null;
  completeLesson: CompleteLesson | null;
}

const initialState: WordState = {
  courseWordLessonDetail: null,
  reviewCourseWordLessonDetail: null,
  lessonResult: null,
  completeLesson: null,
};

export const wordSlice = createSlice({
  name: "word",
  initialState,
  reducers: {
    setCourseWordLessonDetail: (state, action: PayloadAction<CourseWordLessonDetail | null>) => {
      state.courseWordLessonDetail = action.payload ?? null;
    },
    setReviewCourseWordLessonDetail: (
      state,
      action: PayloadAction<CourseWordLessonDetail | null>,
    ) => {
      state.reviewCourseWordLessonDetail = action.payload ?? null;
    },
    updateLessonResult: (state, action: PayloadAction<Partial<LessonResult>>) => {
      state.lessonResult ??= { words: [] };
      state.lessonResult = {
        ...state.lessonResult,
        ...action.payload,
      };
    },
    addOrUpdateWordResult: (state, action: PayloadAction<WordResult>) => {
      const updatedWord = action.payload;

      state.lessonResult ??= { words: [] };

      const index = state.lessonResult.words.findIndex((w) => w.word === updatedWord.word);

      if (index !== -1) {
        state.lessonResult.words[index] = {
          ...state.lessonResult.words[index],
          ...updatedWord,
        };
      } else {
        state.lessonResult.words.push(updatedWord);
      }
    },
    setCompleteLesson: (state, action: PayloadAction<CompleteLesson | null>) => {
      state.completeLesson = action.payload ?? null;
    },
    updateWordExistOnWordbook: (
      state,
      action: PayloadAction<{ wordText: string; existOnWordbook: boolean }>,
    ) => {
      const { wordText, existOnWordbook } = action.payload;

      // Update in courseWordLessonDetail.words
      if (state.courseWordLessonDetail?.words) {
        const wordIndex = state.courseWordLessonDetail.words.findIndex((w) => w.word === wordText);
        if (wordIndex !== -1) {
          state.courseWordLessonDetail.words[wordIndex].existOnWordbook = existOnWordbook;
        }
      }

      // Update in reviewCourseWordLessonDetail.words
      if (state.reviewCourseWordLessonDetail?.words) {
        const wordIndex = state.reviewCourseWordLessonDetail.words.findIndex(
          (w) => w.word === wordText,
        );
        if (wordIndex !== -1) {
          state.reviewCourseWordLessonDetail.words[wordIndex].existOnWordbook = existOnWordbook;
        }
      }

      // Update in lessonResult.words
      if (state.lessonResult?.words) {
        const wordIndex = state.lessonResult.words.findIndex((w) => w.word === wordText);
        if (wordIndex !== -1) {
          state.lessonResult.words[wordIndex].existOnWordbook = existOnWordbook;
        }
      }
    },
    updateWordExistOnWordPractice: (
      state,
      action: PayloadAction<{ wordText: string; existOnWordPractice: boolean }>,
    ) => {
      const { wordText, existOnWordPractice } = action.payload;

      // Update in courseWordLessonDetail.words
      if (state.courseWordLessonDetail?.words) {
        const wordIndex = state.courseWordLessonDetail.words.findIndex((w) => w.word === wordText);
        if (wordIndex !== -1) {
          state.courseWordLessonDetail.words[wordIndex].existOnWordPractice = existOnWordPractice;
        }
      }

      // Update in reviewCourseWordLessonDetail.words
      if (state.reviewCourseWordLessonDetail?.words) {
        const wordIndex = state.reviewCourseWordLessonDetail.words.findIndex(
          (w) => w.word === wordText,
        );
        if (wordIndex !== -1) {
          state.reviewCourseWordLessonDetail.words[wordIndex].existOnWordPractice =
            existOnWordPractice;
        }
      }

      // Update in lessonResult.words
      if (state.lessonResult?.words) {
        const wordIndex = state.lessonResult.words.findIndex((w) => w.word === wordText);
        if (wordIndex !== -1) {
          state.lessonResult.words[wordIndex].existOnWordPractice = existOnWordPractice;
        }
      }
    },
    resetWordState: (state) => {
      state.courseWordLessonDetail = null;
      state.lessonResult = null;
      state.completeLesson = null;
    },
  },
});

export const {
  setCourseWordLessonDetail,
  setReviewCourseWordLessonDetail,
  updateLessonResult,
  addOrUpdateWordResult,
  setCompleteLesson,
  updateWordExistOnWordbook,
  updateWordExistOnWordPractice,
  resetWordState,
} = wordSlice.actions;

export const getCourseWordLessonDetail = (state: RootState) => state.word.courseWordLessonDetail;
export const getReviewCourseWordLessonDetail = (state: RootState) =>
  state.word.reviewCourseWordLessonDetail;
export const getLessonResult = (state: RootState) => state.word.lessonResult;
export const getResultBooleans = (state: RootState): boolean[] =>
  state.word.lessonResult?.words?.map((w) => w.correct ?? false) ?? [];
export const getCompleteLesson = (state: RootState) => state.word.completeLesson;

export default wordSlice.reducer;
